import client from './client';
import { _createAlert } from './apiUtils';
import { TimesheetResponse, Audit } from '../interfaces/AuditInterface';

/**
 * Get audits for a specific employee and pay period
 * @param employeeID - The employee ID (as string, typically altEmployeeID)
 * @param payPeriodID - The pay period ID
 * @returns Array of audit records
 */
export const getEmployeeAudits = async (employeeID: string, payPeriodID: number): Promise<Audit[]> => {
  try {
    if (!employeeID || !payPeriodID) {
      throw new Error("Employee ID and Pay Period ID are required");
    }

    const response = await client.get("/admin/audit/getEmployeeAudit", {
      params: {
        employeeID,
        payPeriodID
      }
    });
    
    if (!response.data) {
      throw new Error("No data returned from server");
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching employee audits:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to fetch audits";
    _createAlert("Error", errorMessage);
    throw error;
  }
};

/**
 * Get all timesheets for a specific employee
 * @param employeeID - The employee ID
 * @returns Array of timesheets with pay period information
 */
export const getEmployeeTimesheets = async (employeeID: number): Promise<TimesheetResponse> => {
  try {
    if (!employeeID) {
      throw new Error("Employee ID is required");
    }

    const response = await client.get(`/admin/employee/${employeeID}/timesheets`);
    
    if (!response.data) {
      throw new Error("No data returned from server");
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching employee timesheets:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to fetch timesheets";
    _createAlert("Error", errorMessage);
    throw error;
  }
};

/**
 * POST a report request that streams back a PDF, verify it really is one, and
 * download it. Shared by the three per-employee report endpoints, which differ
 * only in path, filename and the label used in error messages.
 *
 * `includeComments` is optional and only ever *suppresses* the timecard's
 * Comments row — the server still requires the caller to be Admin/HR/Payroll,
 * so passing true can never grant comments to someone who wouldn't get them.
 */
const downloadReportPdf = async (
  path: string,
  employeeID: number,
  timesheetIDs: number[],
  fileName: string,
  label: string,
  includeComments?: boolean
): Promise<boolean> => {
  try {
    if (!employeeID || !timesheetIDs || timesheetIDs.length === 0) {
      _createAlert("Generate Report Failed", "Employee ID and at least one timesheet ID are required");
      return false;
    }

    const response = await client.post(
      path,
      {
        employeeID,
        timesheetIDs,
        ...(includeComments === undefined ? {} : { includeComments })
      },
      {
        responseType: "arraybuffer"
      }
    );

    // Check if it's a valid PDF (by inspecting first few bytes)
    const byteArray = new Uint8Array(response.data);
    const header = new TextDecoder().decode(byteArray.slice(0, 4));
    if (header !== "%PDF") {
      const text = new TextDecoder().decode(byteArray);
      console.error("Expected PDF, got:", text.slice(0, 300));
      _createAlert("Generate Report Failed", "Server did not return a valid PDF.");
      return false;
    }

    // All good — create and download Blob
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error(`Error generating ${label}:`, error);
    const errorMessage = error instanceof Error ? error.message : `Failed to generate ${label}`;
    _createAlert("Generate Report Failed", errorMessage);
    return false;
  }
};

/**
 * Generate and download an audit report PDF — cover page, then per week the
 * timecard followed by that week's audit records.
 * @param employeeID - The employee ID
 * @param timesheetIDs - Array of timesheet IDs to include in the report
 * @param altEmployeeID - The alternate employee ID for filename
 * @param includeComments - When false, suppresses the timecard's Comments row
 */
export const generateAuditReport = async (
  employeeID: number,
  timesheetIDs: number[],
  altEmployeeID: string,
  includeComments?: boolean
): Promise<boolean> =>
  downloadReportPdf(
    "/admin/audit/generateReport",
    employeeID,
    timesheetIDs,
    `${altEmployeeID}_AuditReport.pdf`,
    "audit report",
    includeComments
  );

/**
 * Generate and download an audit time punch report PDF — cover page, then per
 * week the timecard followed by the colour-coded punch section.
 * @param employeeID - The employee ID
 * @param timesheetIDs - Array of timesheet IDs to include in the report
 * @param altEmployeeID - The alternate employee ID for filename
 * @param includeComments - When false, suppresses the timecard's Comments row
 */
export const generateTimePunchReport = async (
  employeeID: number,
  timesheetIDs: number[],
  altEmployeeID: string,
  includeComments?: boolean
): Promise<boolean> =>
  downloadReportPdf(
    "/admin/audit/generateTimePunchReport",
    employeeID,
    timesheetIDs,
    `${altEmployeeID}_AuditTimePunchReport.pdf`,
    "audit time punch report",
    includeComments
  );

/**
 * Generate and download a timesheet report PDF — the timecards for the selected
 * weeks and nothing else. No cover page, no audit pages.
 * @param employeeID - The employee ID
 * @param timesheetIDs - Array of timesheet IDs to include in the report
 * @param altEmployeeID - The alternate employee ID for filename
 * @param includeComments - When false, suppresses the timecard's Comments row
 */
export const generateTimesheetReport = async (
  employeeID: number,
  timesheetIDs: number[],
  altEmployeeID: string,
  includeComments?: boolean
): Promise<boolean> =>
  downloadReportPdf(
    "/admin/audit/generateTimesheetReport",
    employeeID,
    timesheetIDs,
    `${altEmployeeID}_TimesheetReport.pdf`,
    "timesheet report",
    includeComments
  );
